import type { PostDoc, PromotionDoc } from '@talkofthetown/shared';
import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  where,
  type Timestamp,
} from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { config } from '../config';
import { db } from '../firebase';
import { friendlyError } from '../lib/errors';
import { timeAgo } from '../lib/format';
import { Dialog } from './Dialog';
import { promotionStatusText } from './PromotePanel';

interface Props {
  open: boolean;
  uid: string;
  now: number;
  onClose: () => void;
  onOpenPost: (id: string) => void;
}

type Mine = { id: string; post: PostDoc<Timestamp>; promotion: PromotionDoc | null };

const STATUS: Record<PostDoc['status'], string> = {
  live: 'On the board',
  pending: 'Being checked',
  held: 'Held for review',
  removed: 'Removed',
};

/** The signed-in author's own posts, newest first: how each is doing, and a way back to it. */
export function MyPostsDialog({ open, uid, now, onClose, onOpenPost }: Props) {
  const [items, setItems] = useState<Mine[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    Promise.all([
      getDocs(
        query(
          collection(db, 'posts'),
          where('authorId', '==', uid),
          orderBy('createdAt', 'desc'),
          limit(50),
        ),
      ),
      config.payments
        ? getDocs(query(collection(db, 'promotions'), where('uid', '==', uid)))
        : Promise.resolve(null),
    ])
      .then(([posts, promotions]) => {
        if (cancelled) return;
        const running = new Map<string, PromotionDoc>();
        for (const d of promotions?.docs ?? []) {
          const p = d.data() as PromotionDoc;
          if (p.status === 'review' || p.status === 'active') running.set(p.postId, p);
        }
        setItems(
          posts.docs.map((d) => ({
            id: d.id,
            post: d.data() as PostDoc<Timestamp>,
            promotion: running.get(d.id) ?? null,
          })),
        );
      })
      .catch((err) => !cancelled && setError(friendlyError(err)));
    return () => {
      cancelled = true;
    };
  }, [open, uid]);

  return (
    <Dialog open={open} onClose={onClose} labelledBy="my-posts-title" className="my-posts">
      <h2 id="my-posts-title">My posts</h2>
      {error ? (
        <p role="alert">{error}</p>
      ) : items === null ? (
        <p className="muted">Loading…</p>
      ) : items.length === 0 ? (
        <p>You haven’t posted yet. Press “+ Talk” to start.</p>
      ) : (
        <ul className="review__list">
          {items.map(({ id, post, promotion }) => (
            <li key={id} className="review__item">
              <button
                type="button"
                className="linklike review__title"
                onClick={() => onOpenPost(id)}
              >
                {post.title}
              </button>
              <span className="muted review__meta">
                {STATUS[post.status]} · {timeAgo(post.createdAt.toMillis(), now)}
              </span>
              {promotion && (
                <span className="my-posts__promotion">{promotionStatusText(promotion)}</span>
              )}
            </li>
          ))}
        </ul>
      )}
      {items && items.length > 0 && config.payments && (
        <p className="muted my-posts__note">Open a post to share, promote or delete it.</p>
      )}
    </Dialog>
  );
}
